import { sanitizeExportSlug } from './downloadHelpers.js';

/**
 * Sanitize a logical table key / label for use as the first segment of export filenames.
 * Spaces become `-`; non-safe characters stripped via slug rules.
 *
 * @param {string | null | undefined} tableKey
 * @returns {string}
 */
export function sanitizeTableKeyForFilename(tableKey) {
  const raw = String(tableKey ?? '').trim();
  if (!raw) return 'table';
  return sanitizeExportSlug(raw.replace(/\s+/g, '-'));
}

/**
 * Selected charging station device id (matches CsDiagnostics.toApiJson last item).
 *
 * @param {Record<string, unknown> | null | undefined} csDiagnostics
 * @returns {string}
 */
export function getSelectedCsDeviceId(csDiagnostics) {
  const arr = csDiagnostics?.charging_station;
  if (!Array.isArray(arr) || arr.length === 0) return '';
  const last = arr[arr.length - 1];
  if (last == null || last.device_id == null) return '';
  return String(last.device_id).trim();
}

/**
 * @param {string} value
 * @returns {string}
 */
export function sanitizeCsIdForFilename(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return 'no-cs-id';
  return raw.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '') || 'no-cs-id';
}

/**
 * Normalizes picker/API values to `YYYY-MM-DD_HH-mm-ss` (no spaces).
 *
 * @param {string | number | null | undefined} raw
 * @returns {string}
 */
export function formatDiagnosticsDateTimeSegmentForFilename(raw) {
  if (raw == null || raw === '') return 'unknown';
  let s = String(raw).trim();
  if (!s) return 'unknown';
  s = s.replace('T', ' ');
  s = s.replace(/\.\d{3}Z?$/, '');
  const parts = s.split(/\s+/);
  const datePart = parts[0] || '';
  const timePart = parts[1] || '00:00:00';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return 'unknown';
  }
  const timeNorm = timePart.replace(/:/g, '-').slice(0, 8);
  return `${datePart}_${timeNorm}`;
}

/**
 * @param {string} name
 * @returns {string}
 */
export function sanitizeFullExportFilename(name) {
  return String(name).replace(/[<>:"/\\|?*\u0000-\u001F]/g, '_');
}

/**
 * @param {object} params
 * @param {string | null | undefined} [params.tableKey] prepended segment, e.g. `customer-wise-data` (omit for legacy `{cs}_{from}_{to}` only)
 * @param {string | number} params.csId
 * @param {string | number | null | undefined} params.fromDate
 * @param {string | number | null | undefined} params.toDate
 * @param {string} params.extension `csv` or `xlsx` (no leading dot)
 * @returns {string} full filename including extension
 */
export function buildDiagnosticsExportFilename({ tableKey, csId, fromDate, toDate, extension }) {
  const idSeg = sanitizeCsIdForFilename(csId);
  const fromSeg = formatDiagnosticsDateTimeSegmentForFilename(fromDate);
  const toSeg = formatDiagnosticsDateTimeSegmentForFilename(toDate);
  const ext = String(extension || 'csv').replace(/^\./, '').toLowerCase();
  const safeExt = ext === 'xlsx' || ext === 'csv' ? ext : 'csv';
  const tableSeg =
    tableKey != null && String(tableKey).trim() !== '' ? sanitizeTableKeyForFilename(String(tableKey)) : '';
  const base = tableSeg
    ? `${tableSeg}_${idSeg}_${fromSeg}_${toSeg}.${safeExt}`
    : `${idSeg}_${fromSeg}_${toSeg}.${safeExt}`;
  return sanitizeFullExportFilename(base);
}
